import { Fragment } from 'react';

import { cn } from '@blockworks/ui/utils';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';

export type SelectInputOption = {
    id: string;
    name: string;
    count?: number;
};

type SelectInputProps = {
    // Data
    options: SelectInputOption[];
    // Value
    labelName: string;
    value?: SelectInputOption[] | SelectInputOption;
    multiple?: boolean;
    // Functions
    onChange: (value?: SelectInputOption[] | SelectInputOption) => void;
};

const renderSelectedValueSingle = (item: SelectInputOption) => {
    return (
        <span key={item.id} className="block uppercase text-inherit text-[14px] hover:text-inherit">
            {item.name}
        </span>
    );
};

const renderSelectedValueMultiple = (value: SelectInputOption[]) => {
    return (
        <>
            {value.map(item => {
                return renderSelectedValueSingle(item);
            })}
        </>
    );
};

const SelectInput = (props: SelectInputProps) => {
    // Props
    const {
        // Data
        options,
        // Value
        labelName,
        value,
        multiple,
        // Functions
        onChange,
    } = props;

    // States

    // Variables
    const dataLength = options.length;
    const hasSelectedValue = multiple ? value && (value as SelectInputOption[]).length > 0 : value;

    const onChangeHandler = (selectedValue: SelectInputOption[]) => {
        if (multiple && value) {
            const addedItem = selectedValue[selectedValue.length - 1];
            const currentValue = (value as SelectInputOption[]) || [];
            const itemExists = currentValue.find((item: SelectInputOption) => item.id === addedItem?.id);

            // Clear item if it already exists
            if (itemExists) {
                onChange(currentValue.filter((item: SelectInputOption) => item.id !== addedItem?.id));
                return;
            }
        }

        onChange(selectedValue);
    };

    return (
        <Listbox value={value} onChange={onChangeHandler} multiple={multiple}>
            {({ open }) => (
                <>
                    <div className="relative">
                        <Listbox.Button
                            className={cn(
                                'relative w-full bg-white text-left pr-8 cursor-pointer focus:outline-none  hover:text-black',
                                hasSelectedValue ? ' text-primary' : 'font-light text-light-gray',
                            )}
                        >
                            <span className="flex items-center">
                                {hasSelectedValue ? (
                                    <div className="flex gap-3">
                                        {multiple
                                            ? renderSelectedValueMultiple(value! as SelectInputOption[])
                                            : renderSelectedValueSingle(value as SelectInputOption)}
                                    </div>
                                ) : (
                                    <span className="block uppercase text-inherit text-[14px] hover:text-inherit">
                                        {labelName}
                                        {labelName !== 'Tag' && labelName !== 'Sort' ? (
                                            <span className="text-inherit text-[14px] uppercase ml-3 mr-1">
                                                {dataLength}
                                            </span>
                                        ) : null}
                                    </span>
                                )}
                            </span>
                            <span className="ml-2 absolute inset-y-0 right-0 flex items-center pointer-events-none">
                                <ChevronDownIcon className="text-inherit h-5 w-5 hover:text-inherit aria-hidden=true" />
                            </span>
                        </Listbox.Button>

                        <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <Listbox.Options className="absolute -left-4 z-10 bg-white shadow-lg max-h-56 min-w-[164px] max-w-[164px] w-full rounded-xs text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-xs mt-2">
                                {options?.map(option => (
                                    <Listbox.Option
                                        key={option.id}
                                        className={({ active }) =>
                                            cn(
                                                active ? 'bg-gray-50' : 'text-black text-xs',
                                                'cursor-pointer select-none relative text-xs',
                                            )
                                        }
                                        value={option}
                                    >
                                        {({ selected, active }) => {
                                            const isSelected =
                                                selected ||
                                                (multiple &&
                                                    (value as SelectInputOption[]).find(item => item.id === option.id));

                                            return (
                                                <div className="flex items-center">
                                                    <span
                                                        className={cn(
                                                            'truncate py-3 px-2 pr-9 w-full gap-2 flex',
                                                            active ? 'bg-gray-50' : 'bg-white',
                                                            isSelected ? 'text-primary' : '',
                                                        )}
                                                    >
                                                        <span>
                                                            {isSelected && (
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    strokeWidth={1.5}
                                                                    stroke="currentColor"
                                                                    className="w-4 h-4"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        d="M4.5 12.75l6 6 9-13.5"
                                                                    />
                                                                </svg>
                                                            )}
                                                        </span>
                                                        <span>{option.name}</span>
                                                    </span>
                                                </div>
                                            );
                                        }}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                </>
            )}
        </Listbox>
    );
};

export default SelectInput;
